import {
  Badge,
  Divider,
  Group,
  useMantineTheme,
  useMantineColorScheme,
} from "@mantine/core";
import { useState, useRef } from "react";
import { ApprovalEventTypes } from "./AssetViewer";
import SingleLayout from "./SingleLayout";
import OpenSeadragon from "openseadragon";
import { logger } from "utils/logger";
import classes from "./CompareLayout.module.scss";
import { MarkupEventTypes } from "utils/constants";

const CompareLayout = ({
  selectedAnnotation,
  onImageEvent,
  comparisonItems,
  selectedItems,
  selectedItem,
  selectedFrame,
  settings,
  onCaptureChange,
  settingsChange,
  user,
  onMarkupChange,
  showSettings = true,
}) => {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();

  const img1 =
    comparisonItems && comparisonItems[0] ? comparisonItems[0] : null;
  const img2 =
    comparisonItems && comparisonItems[1] ? comparisonItems[1] : null;

  const [viewer1, setViewer1] = useState(null);
  const [viewer2, setViewer2] = useState(null);

  // Prevent infinite sync loops
  const syncingRef = useRef(false);
  const lastViewportRef = useRef(null);

  const handleViewportChange = (viewportData) => {
    logger.log("handleViewportChange", viewportData);
    if (!settings.syncZoomPan || syncingRef.current) return;

    // Debounce rapid pan events
    const viewportKey = `${viewportData.zoom}-${viewportData.center.x}-${viewportData.center.y}`;
    if (lastViewportRef.current === viewportKey) return;
    lastViewportRef.current = viewportKey;

    logger.log("viewportData", viewportData.eventType);

    // Determine which viewer to sync
    const sourceViewer =
      viewportData.sourceInstanceId === "openSeaDragon1" ? viewer1 : viewer2;
    const targetViewer =
      viewportData.sourceInstanceId === "openSeaDragon1" ? viewer2 : viewer1;

    if (sourceViewer && targetViewer) {
      syncingRef.current = true;

      // Apply same viewport to target viewer
      if (viewportData.eventType === "pan") {
        // For panning, use panTo with the center point
        targetViewer.viewport.panTo(
          new OpenSeadragon.Point(viewportData.center.x, viewportData.center.y),
          false // immediate: false for smooth animation
        );
      } else if (viewportData.eventType === "zoom") {
        // For zooming, first set the zoom level, then center the view
        targetViewer.viewport.zoomTo(viewportData.zoom, null, false);
        // Then center the view at the same relative position
        const centerPoint = new OpenSeadragon.Point(
          viewportData.center.x,
          viewportData.center.y
        );
        targetViewer.viewport.panTo(centerPoint, false);
      } else {
        // For rotation or other events, update both zoom and center
        targetViewer.viewport.zoomTo(
          viewportData.zoom,
          new OpenSeadragon.Point(viewportData.center.x, viewportData.center.y),
          false // immediate: false for smooth animation
        );
      }

      // Reset syncing flag after a short delay
      setTimeout(() => {
        syncingRef.current = false;
      }, 25); // Reduced from 50ms to 25ms for faster response
    }
  };

  logger.log("handleViewportChange", img1, selectedAnnotation);

  return (
    // display debug info for the viewport data
    <>
      <Group
        className={classes.compareWrapper}
        style={{ width: "100%", flexGrow: 1, height: "100%", gap: 0 }}
        px={"md"}
      >
        <div
          className={`${classes.layoutWrapper} ${
            selectedFrame === 0 ? classes.selected : ""
          }`}
          onClick={() => {
            onImageEvent(ApprovalEventTypes.COMPARE_FRAME_CLICK, {
              image: comparisonItems[0],
              index: 0,
            });

            // deselect the annotation if the frame is different from the selected frame
            // if (selectedFrame !== 0 && selectedAnnotation) {
            //   onMarkupChange(MarkupEventTypes.DESELECT_MARKUP, {
            //     image: comparisonItems[0],
            //     payload: null,
            //   });
            // }
          }}
        >
          <SingleLayout
            user={user}
            selectedAnnotation={selectedFrame === 0 ? selectedAnnotation : null }
            onMarkupChange={(type, data) => onMarkupChange(type, data, null)}
            onViewerReady={setViewer1}
            onViewportChange={handleViewportChange}
            image={img1}
            instanceId="openSeaDragon1"
            settings={settings}
            showSettings={showSettings}
            settingsChange={settingsChange}
            onCaptureChange={(type, data, items) =>
              onCaptureChange(type, data, [comparisonItems[0]])
            }
          />

          <Badge
            radius="xl"
            size="md"
            variant="gradient"
            gradient={{ from: "#ed6ea0", to: "#ec8c69", deg: 35 }}
            className={classes.imageBadge}
          >
            1
          </Badge>
          {selectedFrame !== 0 && <div style={{ left:0, right:0, top:0, bottom:0, position: "absolute", backgroundColor: "white", opacity: 0 }}></div>}
        </div>
        <div
          style={{ width: 32, height: "100%", flexGrow: 0, display: "flex" }}
        >
          <Divider
            orientation="vertical"
            style={{ width: 1, marginTop: -16, marginBottom: -16 }}
            mx="md"
          />
        </div>

        <div
          className={`${classes.layoutWrapper} ${
            selectedFrame === 1 ? classes.selected : ""
          }`}
          onClick={() =>
          {

            if(comparisonItems[1]){
              onImageEvent(ApprovalEventTypes.COMPARE_FRAME_CLICK, {
                image: comparisonItems[1],
                index: 1,
              });
  
            }
            
            // deselect the annotation if the frame is different from the selected frame
            // if (selectedFrame !== 1 && selectedAnnotation) {
            //   onMarkupChange(MarkupEventTypes.DESELECT_MARKUP, {
            //     image: comparisonItems[1],
            //     payload: null,
            //   });
            // }
          }}
        
        >
          <SingleLayout
            user={user}
            selectedAnnotation={selectedFrame === 1 ? selectedAnnotation : null}
            onMarkupChange={(type, data) => onMarkupChange(type, data, null)}
            onViewerReady={setViewer2}
            onViewportChange={handleViewportChange}
            image={img2}
            instanceId="openSeaDragon2"
            settings={settings}
            showSettings={showSettings}
            settingsChange={settingsChange}
            onCaptureChange={(type, data, items) =>
              onCaptureChange(type, data, [comparisonItems[1]])
            }
          />

          <Badge
            radius="xl"
            size="md"
            variant="gradient"
            gradient={{ from: "#ed6ea0", to: "#ec8c69", deg: 35 }}
            className={classes.imageBadge}
          >
            2
          </Badge>
          {selectedFrame !== 1 && <div style={{ left:0, right:0, top:0, bottom:0, position: "absolute", backgroundColor: "white", opacity: 0 }}></div>}
        </div>
      </Group>
    </>
  );
};

export default CompareLayout;
